// Returning visitors (Overview card) and weekly retention cohorts (Traffic card).
// Both count only visitors whose id survives from one day to the next, and say so: cookieless
// web visitors get a new id every day, so they can never show up as returning.
import type { Retention, ReturningVisitors } from "../src/retention-types";
import { shared } from "./share";

type Api = <T>(path: string, p?: Record<string, string>) => Promise<T>;
interface Opts {
  api: Api;
  params: Record<string, string>;
  current: () => boolean;
  mobile: boolean;
  errorCard: (err: unknown) => string;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const fmtN = (n: number) => (n >= 10_000 ? `${(n / 1000).toFixed(1)}k` : Math.round(n).toLocaleString("en-US"));
const pct = (r: number | null) => (r === null ? "–" : `${Math.round(r * 100)}%`);
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const dayLabel = (s: string) => `${Number(s.slice(8, 10))} ${MON[Number(s.slice(5, 7)) - 1]}`;

const WHY =
  "Cookieless web visitors get a new ID every day, so they can't be recognised when they come back. Stable IDs come from app installs, signed-in users and web visitors who accepted cookies.";

/** "Based on 120 of 4,300 visitors (3%) with a stable ID." */
function basis(stable: number, visitors: number): string {
  const share = visitors > 0 ? ` (${Math.max(stable > 0 ? 1 : 0, Math.round((stable / visitors) * 100))}%)` : "";
  return `Based on <b class="num">${fmtN(stable)}</b> of ${fmtN(Math.max(visitors, stable))} visitors${share} with a stable ID.`;
}

const UNAVAILABLE = {
  older_than_hot_store: "Only available for ranges within the last 90 days.",
  hot_store_not_configured: "Needs the live store (Analytics Engine), which isn't configured.",
} as const;

// ---------- Overview: new vs returning ----------

/** The card shell, with a slot `renderReturning` fills. Not shown on share links. */
export function returningCard(mobile: boolean): string {
  if (shared) return "";
  const body = `<div id="returning"><div class="skeleton" style="height:${mobile ? 96 : 84}px"></div></div>`;
  return `<section class="card"><div class="card-head"><h2>Returning visitors</h2><span class="spacer"></span>${mobile ? `<span class="hint">Stable IDs</span>` : ""}</div>${body}</section>`;
}

export async function renderReturning(o: Opts): Promise<void> {
  const host = document.getElementById("returning");
  if (!host || shared) return;
  try {
    const r = await o.api<ReturningVisitors>("returning", o.params);
    if (!o.current()) return;
    if (!r.available) {
      host.innerHTML = `<div class="empty">${UNAVAILABLE[r.reason ?? "older_than_hot_store"]}</div>`;
      return;
    }
    if (r.stable === 0) {
      host.innerHTML = `<div class="ret-empty"><b>No visitors with a stable ID in this range.</b> ${WHY}</div>`;
      return;
    }
    const share = (n: number) => n / r.stable;
    const legend = [
      ["Returning", r.returning, "var(--blue)"],
      ["New", r.new, "var(--blue-light)"],
    ] as const;
    host.innerHTML = `<div class="ret-split${o.mobile ? " m" : ""}">
      <div class="stackbar" role="img" aria-label="${pct(share(r.returning))} returning, ${pct(share(r.new))} new">${legend
        .filter(([, n]) => n > 0)
        .map(([, n, c]) => `<div style="width:${(share(n) * 100).toFixed(1)}%;background:${c}"></div>`)
        .join("")}</div>
      <div class="ret-legend">${legend
        .map(([l, n, c]) => `<div><span class="k"><i style="background:${c}"></i>${l}</span><span class="v"><b class="num">${fmtN(n)}</b><span>${pct(share(n))}</span></span></div>`)
        .join("")}</div>
    </div>
    <p class="ret-note">${basis(r.stable, r.visitors)} Returning means first seen before this range started.${r.truncated ? " Too many visitors to count them all; this is a partial count." : ""}
      <span class="ret-why">${WHY}</span></p>`;
  } catch (err) {
    if (o.current()) host.innerHTML = o.errorCard(err);
  }
}

// ---------- Traffic: weekly cohorts ----------

let weeks = 8;

/** The card shell, with a slot `renderRetention` fills. Not shown on share links. */
export function retentionCard(mobile: boolean): string {
  if (shared) return "";
  const seg = `<div class="${mobile ? "mseg s" : "seg small"}" role="group" aria-label="Weeks"${mobile ? ` style="margin-bottom:12px"` : ""}>${[8, 12]
    .map((n) => `<button data-ret-weeks="${n}" aria-pressed="${n === weeks}">${n} weeks</button>`)
    .join("")}</div>`;
  return `<section class="card"><div class="card-head"><h2>Retention</h2>${mobile ? "" : seg}<span class="spacer"></span></div>${mobile ? seg : ""}<div id="retention"><div class="skeleton" style="height:240px"></div></div></section>`;
}

/** Cell background: the brand blue, stronger for higher retention. */
const shade = (r: number) => `color-mix(in srgb, var(--blue) ${Math.round(8 + r * 84)}%, var(--raised))`;

export async function renderRetention(o: Opts & { filtered: boolean }): Promise<void> {
  const host = document.getElementById("retention");
  if (!host || shared) return;
  try {
    const r = await o.api<Retention>("retention", { project: o.params.project!, weeks: String(weeks) });
    if (!o.current()) return;
    if (!r.available) {
      host.innerHTML = `<div class="empty">${UNAVAILABLE[r.reason ?? "older_than_hot_store"]}</div>`;
      return;
    }
    const inCohorts = r.cohorts.reduce((s, c) => s + c.size, 0);
    if (inCohorts === 0) {
      host.innerHTML = `<div class="ret-empty"><b>No visitors with a stable ID first seen in the last ${r.weeks} weeks.</b> ${WHY}</div>`;
      return;
    }
    const n = r.week_starts.length;
    const cols = Array.from({ length: n }, (_, j) => `<th scope="col">${o.mobile ? `W${j}` : `Week ${j}`}</th>`).join("");
    const cell = (rate: number | null, active: number | undefined, size: number, partial: boolean) => {
      if (rate === null) return `<td class="na">–</td>`;
      const dark = rate >= 0.45;
      const title = `${fmtN(active ?? 0)} of ${fmtN(size)}${partial ? " (this week so far)" : ""}`;
      return `<td class="${partial ? "partial " : ""}${dark ? "dark" : ""}" style="background:${shade(rate)}" title="${title}">${pct(rate)}</td>`;
    };
    const rows = r.cohorts
      .map((c, i) => {
        const cells = c.rates.map((rate, j) => cell(rate, c.active[j], c.size, i + j === n - 1)).join("");
        const pad = `<td class="void"></td>`.repeat(n - c.rates.length);
        return `<tr><th scope="row">${dayLabel(c.week)}${i === n - 1 ? `<small>this week</small>` : ""}</th><td class="size num">${fmtN(c.size)}</td>${cells}${pad}</tr>`;
      })
      .join("");
    const avg = r.average.map((a) => (a === null ? `<td class="na">–</td>` : `<td class="avg">${pct(a)}</td>`)).join("");
    host.innerHTML = `<p class="ret-lead">Share of each week's new visitors who were active again in later weeks. The latest column is this week so far (dashed).</p>
      <div class="table-scroll ret-scroll"><table class="cohorts">
        <thead><tr><th scope="col">First visit</th><th scope="col">Visitors</th>${cols}</tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr><th scope="row">Average</th><td class="size"></td>${avg}</tr></tfoot>
      </table></div>
      <p class="ret-note">${basis(r.stable, r.visitors)} Weeks start on Monday; visitors first seen before ${esc(dayLabel(r.week_starts[0]!))} are left out.${o.filtered ? " Filters don't apply here." : ""}${r.truncated ? " Too many visitors to count them all; this is a partial count." : ""}
        <span class="ret-why">${WHY}</span></p>`;
  } catch (err) {
    if (o.current()) host.innerHTML = o.errorCard(err);
  }
}

/** The weeks toggle re-renders only the retention card. */
export function onRetentionWeeks(rerender: () => void): void {
  document.addEventListener("click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-ret-weeks]");
    if (!b) return;
    weeks = Number(b.dataset.retWeeks);
    for (const x of document.querySelectorAll<HTMLElement>("[data-ret-weeks]")) x.setAttribute("aria-pressed", String(x === b));
    rerender();
  });
}
